import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('coinpulse_token'));
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const clearAuth = useCallback(() => {
    localStorage.removeItem('coinpulse_token');
    setToken(null);
    setUser(null);
    setIsPremium(false);
    setIsAdmin(false);
  }, []);

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const userData = response.data;
      setUser(userData);
      setIsPremium(Boolean(userData.premium));
      setIsAdmin(Boolean(userData.admin));
    } catch (error) {
      if (error.response?.status === 401) {
        clearAuth();
      }
      console.error('Failed to fetch user:', error);
    } finally {
      setLoading(false);
    }
  }, [token, clearAuth]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken } = response.data;
    localStorage.setItem('coinpulse_token', newToken);
    setToken(newToken);
    return response.data;
  };

  const signup = async (email, password) => {
    const response = await axios.post(`${API}/auth/signup`, { email, password });
    const { token: newToken } = response.data;
    if (newToken) {
      localStorage.setItem('coinpulse_token', newToken);
      setToken(newToken);
    }
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearAuth();
    }
  };

  const updatePassword = async (currentPassword, newPassword) => {
    const response = await axios.patch(`${API}/auth/update-password`, 
      { current_password: currentPassword, new_password: newPassword },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  };

  const forgotPassword = async (email) => {
    const response = await axios.post(`${API}/auth/forgot-password`, { email });
    return response.data;
  };

  const resetPassword = async (resetToken, password) => {
    const response = await axios.patch(`${API}/auth/reset-password`, { token: resetToken, password });
    return response.data;
  };

  const canViewSignal = (signal) => {
    if (isAdmin) return true;
    // API uses capital P for Premium field
    const signalIsPremium = Boolean(signal?.Premium ?? signal?.premium);
    if (!signalIsPremium) return true;
    return isPremium;
  };

  const value = {
    user,
    token,
    loading,
    isPremium,
    isAdmin,
    login,
    signup,
    logout,
    updatePassword,
    forgotPassword,
    resetPassword,
    canViewSignal,
    clearAuth,
    fetchUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
